import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Download, FileText, Mail, RefreshCw, type LucideIcon } from 'lucide-react';
import { api, type AutomatischData, type AutomatischTaak } from '../lib/api';
import { cn, formatDate } from '../lib/utils';
import { Card, ICON, Notice, Skeleton } from './ui';

/** Instellingen, Automatisch: per onderdeel aan/uit en hoe vaak de app het zelf doet. */

const TAKEN: Array<{ taak: AutomatischTaak; icon: LucideIcon; title: string; sub: string }> = [
  { taak: 'rooster', icon: RefreshCw, title: 'Rooster', sub: 'Diensten uit RostarCAS' },
  { taak: 'gmail', icon: Mail, title: 'Trigion-mail', sub: 'Roosters en urencontroles uit je mail' },
  { taak: 'agenda', icon: CalendarClock, title: 'Google-agenda', sub: "Diensten in 'Werk - Diensten'" },
  { taak: 'afas', icon: Download, title: 'AFAS', sub: 'Loonstroken, urenbijlages en gegevens' },
  { taak: 'archief', icon: FileText, title: 'Mailbijlagen', sub: "PDF's uit je mail naar Documenten" },
];

export function intervalLabel(uren: number): string {
  if (uren === 1) return 'Elk uur';
  if (uren < 24) return `Elke ${uren} uur`;
  if (uren === 24) return 'Elke dag';
  if (uren === 168) return 'Elke week';
  return `Elke ${uren / 24} dagen`;
}

/** Korte tekst voor elders in de app, bijvoorbeeld "Vanzelf elke 8 uur." */
export function vanzelfTekst(data: AutomatischData | undefined, taak: AutomatischTaak): string {
  const t = data?.taken.find((x) => x.taak === taak);
  if (!t) return '';
  return t.aan ? `Vanzelf ${intervalLabel(t.elke_uren).toLowerCase()}.` : 'Automatisch staat uit.';
}

export function AutomatischSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['automatisch'], queryFn: api.automatisch.list });
  const mut = useMutation({
    mutationFn: (v: { taak: AutomatischTaak; aan?: boolean; elke_uren?: number }) => api.automatisch.update(v.taak, v),
    onSuccess: (d) => qc.setQueryData(['automatisch'], d),
  });

  if (isLoading || !data) {
    return (
      <div className="card space-y-3" aria-busy="true" aria-label="Laden">
        <Skeleton className="h-4 w-32" />
        {TAKEN.map((t) => (
          <Skeleton key={t.taak} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <Card flush title="Automatisch ophalen" icon={RefreshCw}>
      <div className="list border-t border-surface-line">
        {TAKEN.map(({ taak, icon: Icon, title, sub }) => {
          const t = data.taken.find((x) => x.taak === taak);
          if (!t) return null;
          const opties = data.intervallen[taak] ?? [];
          const meta = !t.aan
            ? 'Alleen via Acties'
            : t.volgende && Date.parse(t.volgende) > Date.now()
              ? `Volgende ${formatDate(t.volgende, 'dd-MM HH:mm')}`
              : 'Volgende binnen het uur';
          const laatst = t.laatst_gedraaid ? `Laatst automatisch: ${formatDate(t.laatst_gedraaid, 'dd-MM HH:mm')}` : 'Nog niet automatisch gedraaid';
          return (
            <div key={taak} className="row pr-3 sm:pr-4">
              <Icon className={cn(ICON.sm, 'shrink-0 text-slate-400')} aria-hidden />
              <div className="min-w-0 flex-1">
                <label htmlFor={`auto-${taak}`} className="block truncate text-sm font-medium text-slate-100">
                  {title}
                </label>
                <div className="truncate text-xs text-slate-400" title={`${sub}. ${laatst}.`}>
                  {meta}
                </div>
              </div>
              <select
                id={`auto-${taak}`}
                className="input w-36 shrink-0 py-1.5 text-sm"
                value={t.aan ? String(t.elke_uren) : '0'}
                disabled={mut.isPending}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  mut.mutate(v === 0 ? { taak, aan: false } : { taak, aan: true, elke_uren: v });
                }}
              >
                {opties.map((u) => (
                  <option key={u} value={u}>
                    {intervalLabel(u)}
                  </option>
                ))}
                <option value="0">Uit</option>
              </select>
            </div>
          );
        })}
      </div>
      <div className="space-y-2 border-t border-surface-line px-4 py-3 sm:px-5">
        {mut.isError && <Notice tone="err">Opslaan mislukt, probeer het opnieuw.</Notice>}
        <p className="text-xs text-slate-500">
          Wijzigingen gelden meteen. Vaker ophalen kost niets extra, alleen nieuwe mails en documenten worden gelezen.
        </p>
      </div>
    </Card>
  );
}
